"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.functionNode = exports.validArgumentCount = void 0;
const common_1 = require("@finley-technologies/common");
const utils_1 = require("../../../utils");
const functions_1 = require("../../functions");
const validArgumentCount = ({ count, required: { min, increment, max }, }) => {
    // Exact match
    if (max !== undefined && max === min && count !== max)
        return false;
    // Not enough arguments
    if (count < min)
        return false;
    // Too many arguments
    if (max !== undefined && count > max)
        return false;
    // If increment is provided, start from min and use modulo operator
    if (increment !== undefined && (count - min) % increment !== 0)
        return false;
    // Valid count
    return true;
};
exports.validArgumentCount = validArgumentCount;
const functionEvaluationCache = {};
exports.functionNode = {
    hasChildNodes: true,
    getEvaluatorFunction: ({ node, buildContext }) => {
        // Retrieve registered function
        const functionName = node.name;
        const registeredFunction = (0, functions_1.getRegisteredFunction)(functionName);
        // If the function is not array compatible, validate that none of the node arguments represent a range variable
        // TODO: validate array compatibility
        // Determine the number of inputs required for this function based on the formula definition
        const requiredInputCount = node.arguments.length;
        // Validate that the input formula is using the correct number of inputs according to the function definition
        (0, common_1.finleyAssert)((0, exports.validArgumentCount)({
            count: requiredInputCount,
            required: registeredFunction.information.requiredInputs,
        }), 'Invalid number of inputs for function');
        // Evaluator functions must be popped off the stack for each argument provided for the node
        const functionStackItems = new Array(node.arguments.length)
            .fill(null)
            .map(() => {
            const functionStackItem = buildContext.functionStack.pop();
            (0, common_1.finleyAssert)(functionStackItem, 'Formula generation failure');
            return functionStackItem;
        });
        // Reverse because the stack pops from right to left, but the evaluation function queue must be ordered left to right
        functionStackItems.reverse();
        // Function evaluation caching (necessary to avoid performance issues with range formulas)
        const cacheKeys = [];
        const allowCache = node.arguments.every((childNode) => {
            const nodeContent = (0, utils_1.getNodeContent)(childNode);
            cacheKeys.push(nodeContent);
            switch (childNode.type) {
                case 'binary-expression': // Always evaluated on demand with row variables (not range compatible)
                    return false;
                case 'cell': // Cache only when range variable is involved
                    return !!(0, utils_1.isRangeVariable)(nodeContent);
                case 'cell-range': // not handled
                    return false;
                case 'function': // If child node is a function, it must handle its own cache and return that
                    return false;
                case 'logical': // Fixed values, always able to cache
                    return true;
                case 'number': // Fixed values, always able to cache
                    return true;
                case 'text': // Fixed values, always able to cache
                    return true;
                case 'unary-expression': // not handled
                    return false;
                default:
                    return (0, common_1.finleyAssertUnreachable)(childNode);
            }
        });
        const cacheKey = cacheKeys.join('__cachekeyseparator__');
        return (evaluatorInput) => {
            if (allowCache) {
                const cachedResult = functionEvaluationCache[functionName]?.[cacheKey];
                if (cachedResult) {
                    return cachedResult;
                }
            }
            const inputNodes = [];
            const inputValues = [];
            // Handle input nodes
            // eslint-disable-next-line no-restricted-syntax
            for (let idx = 0; idx < functionStackItems.length; idx += 1) {
                const { evaluatorFunction, node: childNode } = functionStackItems[idx];
                // Optional short circuit handler
                if (registeredFunction.shortCircuitHandler) {
                    const shortCircuitResult = registeredFunction.shortCircuitHandler({
                        idx,
                        lastValue: inputValues.length
                            ? { result: inputValues[inputValues.length - 1] }
                            : null,
                    });
                    if (shortCircuitResult.token) {
                        inputNodes.push({
                            node: childNode,
                            inputs: [],
                            output: { error: null, result: shortCircuitResult.token },
                        });
                        inputValues.push(shortCircuitResult.token);
                        continue;
                    }
                }
                const evaluatedNode = evaluatorFunction(evaluatorInput);
                inputNodes.push(evaluatedNode);
                if (evaluatedNode.output.error === null) {
                    inputValues.push(evaluatedNode.output.result);
                    continue;
                }
                if (registeredFunction.errorHandler) {
                    const errorHandlerResult = registeredFunction.errorHandler({
                        idx,
                        error: evaluatedNode.output.error,
                    });
                    // If token was provided, prevent propagation of handled error and replace value
                    if (errorHandlerResult.token) {
                        inputValues.push(errorHandlerResult.token);
                        continue;
                    }
                }
                // Error has not been handled, early exit with propagation of error
                const result = {
                    node,
                    inputs: inputNodes,
                    output: evaluatedNode.output,
                };
                if (allowCache) {
                    const cachedFunctionResult = functionEvaluationCache[functionName] ?? {};
                    cachedFunctionResult[cacheKey] = result;
                    functionEvaluationCache[functionName] = cachedFunctionResult;
                }
                return result;
            }
            const output = registeredFunction.evaluate(inputValues);
            const result = {
                node,
                inputs: inputNodes,
                output,
            };
            if (allowCache) {
                const cachedFunctionResult = functionEvaluationCache[functionName] ?? {};
                cachedFunctionResult[cacheKey] = result;
                functionEvaluationCache[functionName] = cachedFunctionResult;
            }
            return result;
        };
    },
};
//# sourceMappingURL=function.js.map